import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { FolderCounts } from '@/hooks/use-folder-counts';
import {
  AlertCircle,
  Crown,
  ShoppingCart,
  Inbox,
  Clock,
  AlertTriangle,
  CheckCircle,
  Folder,
  Plus,
  MoreVertical,
  Edit,
  Trash,
  User,
  AlarmClock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InboxFolder, inboxFolderService } from '@socialflows/shared-lib';
import { FolderDialog } from './folder-dialog';
import { useToast } from '@/hooks/use-toast';
import { useInbox } from '../inbox-context';

interface FolderItem {
  id: string;
  label: string;
  icon: LucideIcon;
  count?: number;
  color?: 'red' | 'amber' | 'green';
}

interface InboxSidebarProps {
  selectedFolder: string;
  counts: FolderCounts;
  loading: boolean;
  onFolderSelect: (folder: string) => void;
  highlightedFolder?: string | null;
  className?: string;
}

function FolderLink({
  folder,
  isActive,
  isHighlighted,
  onClick,
}: {
  folder: FolderItem;
  isActive: boolean;
  isHighlighted?: boolean;
  onClick: () => void;
}) {
  const Icon = folder.icon;

  // Color classes for high-profile folders
  const colorClasses = {
    red: {
      active:
        'bg-red-100 text-red-900 border-red-300 dark:bg-red-950 dark:text-red-100 dark:border-red-800',
      hover: 'hover:bg-red-50 dark:hover:bg-red-950/50',
      badge: 'bg-red-600 text-white',
    },
    amber: {
      active:
        'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-800',
      hover: 'hover:bg-amber-50 dark:hover:bg-amber-950/50',
      badge: 'bg-amber-600 text-white',
    },
    green: {
      active:
        'bg-green-100 text-green-900 border-green-300 dark:bg-green-950 dark:text-green-100 dark:border-green-800',
      hover: 'hover:bg-green-50 dark:hover:bg-green-950/50',
      badge: 'bg-green-600 text-white',
    },
  };

  const activeClass = folder.color
    ? colorClasses[folder.color].active
    : 'bg-secondary text-secondary-foreground';

  const hoverClass = folder.color ? colorClasses[folder.color].hover : 'hover:bg-secondary/50';

  const badgeClass =
    folder.color && isActive ? colorClasses[folder.color].badge : 'bg-muted text-muted-foreground';

  const highlightColorMap: Record<string, string> = {
    red: '0 84% 60%',
    amber: '38 92% 50%',
    green: '142 71% 45%',
  };

  const highlightStyle = isHighlighted
    ? { '--highlight-hsl': highlightColorMap[folder.color ?? ''] ?? '217 91% 60%' }
    : undefined;

  return (
    <button
      onClick={onClick}
      style={highlightStyle as React.CSSProperties}
      className={cn(
        'flex items-center justify-between w-full px-3 py-2 text-sm rounded-md transition-all relative',
        isActive ? activeClass : hoverClass,
        isHighlighted && 'animate-folder-highlight'
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <span>{folder.label}</span>
      </div>
      {folder.count !== undefined && (
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', badgeClass)}>
          {folder.count}
        </span>
      )}
    </button>
  );
}

function SidebarSkeleton() {
  return (
    <div className="space-y-4 p-4">
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
      <div className="border-t pt-4 space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    </div>
  );
}

export function InboxSidebar({
  selectedFolder,
  counts,
  loading,
  onFolderSelect,
  highlightedFolder,
  className,
}: InboxSidebarProps) {
  const { toast } = useToast();
  const { customFolders, loadingFolders } = useInbox();
  const [localCustomFolders, setLocalCustomFolders] = useState<InboxFolder[]>(customFolders);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<InboxFolder | undefined>();

  // Sync context folders to local state (for optimistic updates)
  useEffect(() => {
    setLocalCustomFolders(customFolders);
  }, [customFolders]);

  const handleCreateFolder = () => {
    setEditingFolder(undefined);
    setFolderDialogOpen(true);
  };

  const handleEditFolder = (folder: InboxFolder) => {
    setEditingFolder(folder);
    setFolderDialogOpen(true);
  };

  const handleDeleteFolder = async (folder: InboxFolder) => {
    if (!confirm(`Are you sure you want to delete "${folder.name}"?`)) return;

    try {
      await inboxFolderService.delete(folder.id);
      setLocalCustomFolders((prev) => prev.filter((f) => f.id !== folder.id));
      toast({
        title: 'Folder deleted',
        description: 'Your folder has been deleted successfully.',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete folder. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleFolderSuccess = (folder: InboxFolder) => {
    if (editingFolder) {
      // Update existing
      setLocalCustomFolders((prev) => prev.map((f) => (f.id === folder.id ? folder : f)));
    } else {
      // Add new
      setLocalCustomFolders((prev) => [...prev, folder]);
    }
  };

  if (loading) {
    return (
      <div className={cn('w-64 border-r bg-background', className)}>
        <SidebarSkeleton />
      </div>
    );
  }

  const highProfileFolders: FolderItem[] = [
    {
      id: 'urgent',
      label: 'Urgent',
      icon: AlertCircle,
      count: counts.urgent,
      color: 'red',
    },
    {
      id: 'vip',
      label: 'VIP Customers',
      icon: Crown,
      count: counts.vip,
      color: 'amber',
    },
    {
      id: 'buyers',
      label: 'Buyers',
      icon: ShoppingCart,
      count: counts.buyers,
      color: 'green',
    },
  ];

  const standardFolders: FolderItem[] = [
    {
      id: 'all',
      label: 'Inbox',
      icon: Inbox,
      count: counts.all,
    },
    {
      id: 'reminders',
      label: 'Reminders',
      icon: AlarmClock,
      count: counts.reminders,
    },
    {
      id: 'myTickets',
      label: 'My Tickets',
      icon: User,
      count: counts.myTickets,
    },
    {
      id: 'backlog',
      label: 'Backlog',
      icon: Clock,
      count: counts.backlog,
    },
    {
      id: 'spam',
      label: 'Spam',
      icon: AlertTriangle,
      count: counts.spam,
    },
    {
      id: 'archived',
      label: 'Closed',
      icon: CheckCircle,
    },
  ];

  return (
    <div className={cn('w-64 border-r bg-background overflow-y-auto', className)}>
      <div className="p-4 space-y-4">
        {/* High-profile folders section */}
        <div className="space-y-1">
          {highProfileFolders.map((folder) => (
            <FolderLink
              key={folder.id}
              folder={folder}
              isActive={selectedFolder === folder.id}
              isHighlighted={highlightedFolder === folder.id}
              onClick={() => onFolderSelect(folder.id)}
            />
          ))}
        </div>

        {/* Separator */}
        <div className="border-t" />

        {/* Standard folders section */}
        <div className="space-y-1">
          {standardFolders.map((folder) => (
            <FolderLink
              key={folder.id}
              folder={folder}
              isActive={selectedFolder === folder.id}
              isHighlighted={highlightedFolder === folder.id}
              onClick={() => onFolderSelect(folder.id)}
            />
          ))}
        </div>

        {/* Custom folders section */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase">
              Custom Folders
            </span>
            <Button variant="ghost" size="sm" onClick={handleCreateFolder} className="h-6 w-6 p-0">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {loadingFolders ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : localCustomFolders.length === 0 ? (
            <div className="py-6 text-center">
              <Folder className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground mb-2">No custom folders yet</p>
              <Button variant="outline" size="sm" onClick={handleCreateFolder}>
                <Plus className="mr-2 h-4 w-4" />
                Create Folder
              </Button>
            </div>
          ) : (
            <div className="space-y-1">
              {localCustomFolders.map((folder) => (
                <div key={folder.id} className="flex items-center justify-between group">
                  <button
                    onClick={() => onFolderSelect(`custom:${folder.id}`)}
                    style={
                      highlightedFolder === `custom:${folder.id}`
                        ? ({ '--highlight-hsl': '217 91% 60%' } as React.CSSProperties)
                        : undefined
                    }
                    className={cn(
                      'flex-1 flex items-center justify-between px-3 py-2 text-sm rounded-md transition-all text-left',
                      selectedFolder === `custom:${folder.id}`
                        ? 'bg-secondary text-secondary-foreground'
                        : 'hover:bg-secondary/50',
                      highlightedFolder === `custom:${folder.id}` && 'animate-folder-highlight'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Folder className="h-4 w-4" />
                      <span className="truncate">{folder.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {folder.visibility === 'SHARED' && (
                        <span className="text-xs text-muted-foreground">Shared</span>
                      )}
                      {counts?.customFolders?.[folder.id] !== undefined &&
                        counts.customFolders[folder.id] > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                            {counts.customFolders[folder.id]}
                          </span>
                        )}
                    </div>
                  </button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditFolder(folder)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteFolder(folder)}
                        className="text-destructive"
                      >
                        <Trash className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <FolderDialog
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        folder={editingFolder}
        onSuccess={handleFolderSuccess}
      />
    </div>
  );
}
